(() => {
  const SKU_MATCH_RATIO = 0.6;
  const SKU_CACHE_KEY = 'feli-sku-index-v3';
  const SKU_CACHE_TTL = 15 * 60 * 1000;
  let skuIndexPromise = null;

  const normaliseWord = (word) => ({
    handbags: 'handbag',
    bags: 'bag',
    backpacks: 'backpack',
    purses: 'wallet',
    shoppers: 'shopper',
    totes: 'tote',
    wallets: 'wallet',
    gray: 'grey'
  }[word] || word);

  const cleanQuery = (value) => value
    .toLowerCase()
    .replace(/title:/g, ' ')
    .replace(/\b(back[ -]?pack)\b/g, 'backpack')
    .replace(/\b(hand[ -]?bag)\b/g, 'handbag')
    .replace(/\b(cross[ -]?body)\b/g, 'sling')
    .replace(/\b(i need|i want|i am looking for|i'm looking for|i am after|i'm after|show me|looking for|please|can you find|do you have|i would like|i'd like)\b/g, ' ')
    .replace(/\b(bag for work|work bag)\b/g, 'laptop bag')
    .replace(/\b(stylish|stylish-looking|elegant|beautiful|lovely|cute|nice|fashionable)\b/g, ' ')
    .replace(/[^a-z0-9 -]/g, ' ')
    .split(/\s+/)
    .filter((word) => word && !['a', 'an', 'the', 'for', 'me', 'some', 'find', 'with', 'and', 'to', 'of'].includes(word))
    .map(normaliseWord)
    .join(' ')
    .trim();

  const normaliseSku = (value) => String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');

  const isSkuCandidate = (value) => {
    const compact = normaliseSku(value);
    return compact.length >= 4 && /[0-9]/.test(compact);
  };

  const skuKeys = (sku) => {
    const parts = String(sku || '').split(/[\/+&,;]/);
    const keys = [normaliseSku(sku)];
    parts.forEach((part) => {
      keys.push(normaliseSku(part));
      part.trim().split(/\s+/).forEach((token) => {
        if (/\d/.test(token)) keys.push(normaliseSku(token));
      });
    });
    return [...new Set(keys.filter((key) => key.length >= 4))];
  };

  const escapeHtml = (value) => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const rootUrl = () => window.Shopify?.routes?.root || '/';

  const readCachedSkuIndex = () => {
    try {
      const cached = JSON.parse(window.sessionStorage.getItem(SKU_CACHE_KEY));
      if (cached?.createdAt && Date.now() - cached.createdAt < SKU_CACHE_TTL && Array.isArray(cached.items)) return cached.items;
    } catch (error) {
      return null;
    }
    return null;
  };

  const buildSkuIndex = async () => {
    const cached = readCachedSkuIndex();
    if (cached) return cached;
    const fetchPage = async (page) => {
      const response = await fetch(`${rootUrl()}collections/all?view=sku-index&page=${page}`, { credentials: 'same-origin' });
      if (!response.ok) throw new Error('SKU index request failed');
      return response.json();
    };
    const firstPage = await fetchPage(1);
    const pageCount = Math.min(Number(firstPage.pages) || 1, 40);
    const remainingPages = pageCount > 1
      ? await Promise.all(Array.from({ length: pageCount - 1 }, (_, index) => fetchPage(index + 2)))
      : [];
    const items = [];
    [firstPage, ...remainingPages].forEach((payload) => {
      const products = Array.isArray(payload.products) ? payload.products : [];
      products.forEach((product) => {
        (product.variants || []).forEach((variant) => {
          if (!variant.sku) return;
          items.push({
            handle: product.handle,
            title: product.title,
            image: product.image || '',
            price: Number(variant.priceCents || 0) / 100,
            available: Boolean(variant.available),
            sku: variant.sku,
            keys: skuKeys(variant.sku)
          });
        });
      });
    });
    try {
      window.sessionStorage.setItem(SKU_CACHE_KEY, JSON.stringify({ createdAt: Date.now(), items }));
    } catch (error) {
      return items;
    }
    return items;
  };

  const loadSkuIndex = () => {
    if (!skuIndexPromise) skuIndexPromise = buildSkuIndex().catch(() => []);
    return skuIndexPromise;
  };

  const findSkuMatches = async (value) => {
    const query = normaliseSku(value);
    if (!isSkuCandidate(value)) return [];
    const items = await loadSkuIndex();
    const matches = [];
    items.forEach((item) => {
      let bestScore = 0;
      item.keys.forEach((key) => {
        if (!key.includes(query)) return;
        const score = query.length / key.length;
        if (score >= SKU_MATCH_RATIO) bestScore = Math.max(bestScore, score);
      });
      if (bestScore > 0) matches.push({ ...item, score: bestScore });
    });
    matches.sort((a, b) => b.score - a.score || Number(b.available) - Number(a.available) || a.title.localeCompare(b.title));
    const unique = [];
    const handles = new Set();
    matches.forEach((item) => {
      if (handles.has(item.handle)) return;
      handles.add(item.handle);
      unique.push(item);
    });
    return unique.slice(0, 8);
  };

  const money = (value) => {
    const currency = window.Shopify?.currency?.active || 'ZAR';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-ZA', { style: 'currency', currency }).format(Number(value));
    } catch (error) {
      return `${currency} ${value}`;
    }
  };

  const skuResultsMarkup = (matches) => `
    <div class="feli-predictive__panel" role="listbox" aria-label="Search suggestions">
      <ul class="feli-predictive__list" role="presentation">
        ${matches.map((item) => `
          <li role="option">
            <a href="${rootUrl()}products/${encodeURIComponent(item.handle)}">
              <span class="feli-predictive__image">${item.image ? `<img src="${escapeHtml(item.image)}&width=160" alt="" width="80" height="80" loading="lazy">` : ''}</span>
              <span class="feli-predictive__meta">
                <strong>${escapeHtml(item.title)}</strong>
                <small>${escapeHtml(item.sku)} · ${escapeHtml(money(item.price))}</small>
              </span>
            </a>
          </li>
        `).join('')}
      </ul>
    </div>`;

  const renderFormSkuResults = (form, matches) => {
    const component = form.closest('feli-predictive-search');
    const existingResults = component?.querySelector('[data-feli-predictive-results]');
    if (existingResults) {
      existingResults.innerHTML = skuResultsMarkup(matches);
      existingResults.hidden = false;
      component.querySelector('input[type="search"]')?.setAttribute('aria-expanded', 'true');
      return;
    }
    let results = form.parentElement.querySelector(':scope > .feli-sku-results');
    if (!results) {
      results = document.createElement('div');
      results.className = 'feli-predictive feli-sku-results';
      form.insertAdjacentElement('afterend', results);
    }
    results.innerHTML = skuResultsMarkup(matches);
    results.hidden = false;
    results.querySelector('a')?.focus();
  };

  document.querySelectorAll('[data-feli-search-form]').forEach((form) => {
    form.addEventListener('submit', async (event) => {
      const input = form.querySelector('input[name="q"]');
      const rawValue = input.value.trim();
      if (isSkuCandidate(rawValue)) {
        event.preventDefault();
        form.setAttribute('aria-busy', 'true');
        const matches = await findSkuMatches(rawValue);
        form.removeAttribute('aria-busy');
        if (matches.length === 1) {
          window.location.assign(`${rootUrl()}products/${encodeURIComponent(matches[0].handle)}`);
          return;
        }
        if (matches.length > 1) {
          renderFormSkuResults(form, matches);
          return;
        }
        const normalisedFallback = cleanQuery(rawValue);
        if (normalisedFallback) input.value = normalisedFallback;
        HTMLFormElement.prototype.submit.call(form);
        return;
      }
      const normalised = cleanQuery(rawValue);
      if (normalised) input.value = normalised;
    });
  });

  if (!customElements.get('feli-predictive-search')) {
    customElements.define('feli-predictive-search', class extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('input[type="search"]');
        this.results = this.querySelector('[data-feli-predictive-results]');
        this.controller = null;
        this.timer = null;
        this.requestToken = 0;
        this.input.addEventListener('input', () => this.onInput());
        this.input.addEventListener('keydown', (event) => this.onKeydown(event));
        document.addEventListener('click', (event) => {
          if (!this.contains(event.target)) this.close();
        });
      }

      onInput() {
        window.clearTimeout(this.timer);
        this.requestToken += 1;
        const rawValue = this.input.value.trim();
        const query = cleanQuery(rawValue);
        if (query.length < 2) {
          this.close();
          return;
        }
        if (isSkuCandidate(rawValue)) {
          this.timer = window.setTimeout(() => this.fetchSkuResults(rawValue, this.requestToken), 180);
          return;
        }
        this.timer = window.setTimeout(() => this.fetchResults(query), 180);
      }

      async fetchSkuResults(rawValue, token) {
        const matches = await findSkuMatches(rawValue);
        if (token !== this.requestToken) return;
        if (!matches.length) {
          this.fetchResults(cleanQuery(rawValue));
          return;
        }
        if (this.controller) this.controller.abort();
        this.results.innerHTML = skuResultsMarkup(matches);
        this.results.hidden = false;
        this.input.setAttribute('aria-expanded', 'true');
      }

      async fetchResults(query) {
        if (this.controller) this.controller.abort();
        this.controller = new AbortController();
        const params = new URLSearchParams({
          q: query,
          section_id: this.dataset.sectionId,
          'resources[type]': 'query,product,collection',
          'resources[limit]': '6',
          'resources[options][unavailable_products]': 'last',
          'resources[options][fields]': 'title,product_type,variants.title,variants.sku,vendor,tag'
        });
        try {
          const response = await fetch(`${rootUrl()}search/suggest?${params}`, { signal: this.controller.signal });
          if (!response.ok) throw new Error('Predictive search request failed');
          const html = await response.text();
          const documentFragment = new DOMParser().parseFromString(html, 'text/html');
          const section = documentFragment.querySelector(`#shopify-section-${this.dataset.sectionId}`);
          this.results.innerHTML = section ? section.innerHTML : '';
          this.results.hidden = !this.results.innerHTML.trim();
          this.input.setAttribute('aria-expanded', String(!this.results.hidden));
          this.results.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '0'));
        } catch (error) {
          if (error.name !== 'AbortError') this.close();
        }
      }

      onKeydown(event) {
        if (event.key === 'Escape') {
          this.close();
          this.input.focus();
        }
      }

      close() {
        this.results.hidden = true;
        this.results.innerHTML = '';
        this.input.setAttribute('aria-expanded', 'false');
      }
    });
  }

  document.querySelectorAll('[data-feli-filter-toggle]').forEach((button) => {
    button.addEventListener('click', () => {
      const filters = document.getElementById(button.getAttribute('aria-controls'));
      const open = button.getAttribute('aria-expanded') === 'true';
      button.setAttribute('aria-expanded', String(!open));
      filters?.classList.toggle('is-open', !open);
    });
  });
})();
